'use client';

import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '~/lib/utils';
import { Typography } from '~/shared/components/Typography';
import { useIsolatedSearch } from '~/shared/hooks/useIsolatedSearch';

export interface SearchableSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  onSearchChange?: (searchTerm: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  icon?: React.ReactNode;
  name?: string;
  isLoading?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  onSearchChange,
  placeholder = 'Type to search or select...',
  emptyMessage = 'No options found.',
  disabled = false,
  className,
  icon,
  name,
  isLoading = false
}: SearchableSelectProps) {
  // Estados isolados para esta instância
  const [isOpen, setIsOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  // Refs para controle
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Hook de busca isolado
  const isolatedSearch = useIsolatedSearch<SearchableSelectOption>({
    debounceMs: 300,
    onSearch: onSearchChange
  });

  // Determinar se é busca externa ou local
  const hasExternalSearch = Boolean(onSearchChange);

  // Valor exibido no input
  const selectedOption = React.useMemo(() => {
    return options.find(option => option.value === value);
  }, [options, value]);

  const displayValue = React.useMemo(() => {
    if (isFocused) {
      return isolatedSearch.searchTerm;
    }
    return selectedOption?.label || '';
  }, [isFocused, isolatedSearch.searchTerm, selectedOption]);

  // Opções para exibir no dropdown
  const displayOptions = React.useMemo(() => {
    if (hasExternalSearch) {
      // Para busca externa, usar as opções fornecidas (já filtradas pela API)
      return options;
    } else {
      // Para busca local, usar o filtro isolado
      return isolatedSearch.filterLocalData(options, isolatedSearch.searchTerm);
    }
  }, [options, isolatedSearch, hasExternalSearch]);

  // Handler para mudança no input
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    isolatedSearch.setSearchTerm(newValue);

    // Abrir dropdown se tem conteúdo
    if (newValue.trim() && !isOpen) {
      setIsOpen(true);
    }

    // Se string vazia, limpar seleção e fechar dropdown
    if (!newValue.trim()) {
      if (value) {
        onValueChange?.('');
      }
      setIsOpen(false);
    }

    // Usar busca isolada
    isolatedSearch.search(newValue);
  };

  // Handler para foco no input
  const handleInputFocus = () => {
    setIsFocused(true);

    // Se há valor selecionado, mostrar o termo de busca limpo para edição
    if (selectedOption) {
      isolatedSearch.setSearchTerm('');
    }

    // Carregar dados iniciais se necessário
    if (hasExternalSearch && options.length === 0) {
      isolatedSearch.loadInitialData();
    }

    // Abrir dropdown se há opções ou se permite busca
    if (options.length > 0 || hasExternalSearch) {
      setIsOpen(true);
    }
  };

  // Handler para blur no input
  const handleInputBlur = () => {
    // Delay para permitir clique em opções
    setTimeout(() => {
      setIsFocused(false);

      // Se não há valor selecionado, limpar o input
      if (!value) {
        isolatedSearch.setSearchTerm('');
      }

      setIsOpen(false);
    }, 150);
  };

  // Handler para seleção de opção
  const handleOptionSelect = (optionValue: string) => {
    const option = options.find(opt => opt.value === optionValue);

    if (option) {
      onValueChange?.(optionValue);
      isolatedSearch.setSearchTerm(option.label);
      setIsOpen(false);
      setIsFocused(false);

      // Focar no input brevemente e depois remover o foco
      inputRef.current?.focus();
      setTimeout(() => {
        inputRef.current?.blur();
      }, 50);
    }
  };

  // Handler para teclas
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (displayOptions.length === 1) {
        handleOptionSelect(displayOptions[0].value);
      }
    }

    if (event.key === 'ArrowDown' && !isOpen) {
      setIsOpen(true);
    }
  };

  // Fechar quando clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset do termo de busca quando o valor externo muda
  React.useEffect(() => {
    if (!isFocused && selectedOption) {
      isolatedSearch.setSearchTerm(selectedOption.label);
    }
  }, [selectedOption, isFocused, isolatedSearch]);

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {name && <input type="hidden" name={name} value={value || ''} />}

      <div className="relative">
        <div className="flex items-center">
          {icon && (
            <span className="absolute left-3 z-10 flex-shrink-0 text-muted-foreground">
              {icon}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={
              isLoading || isolatedSearch.isSearching
                ? 'Loading...'
                : placeholder
            }
            className={cn(
              'flex h-9 w-full rounded-md border border-input bg-transparent py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
              icon ? 'pl-10 pr-8' : 'pl-3 pr-8',
              isOpen && 'ring-1 ring-ring',
              isLoading && 'opacity-50'
            )}
            aria-haspopup="listbox"
            autoComplete="off"
          />
          <div className="absolute right-2 flex items-center pointer-events-none">
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-popover text-popover-foreground shadow-md rounded-md border max-h-[200px] overflow-y-auto">
          {isLoading || isolatedSearch.isSearching ? (
            <div className="py-6 text-center">
              <Typography variant="body-sm-regular" color="light">
                Loading...
              </Typography>
            </div>
          ) : displayOptions.length > 0 ? (
            <div className="p-1">
              {displayOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleOptionSelect(option.value)}
                  disabled={option.disabled}
                  className={cn(
                    'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50',
                    value === option.value && 'bg-accent text-accent-foreground'
                  )}>
                  <Typography variant="body-sm-regular" className="truncate">
                    {option.label}
                  </Typography>
                  {value === option.value && (
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center">
              <Typography variant="body-sm-regular" color="light">
                {emptyMessage}
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
